// src/lib/api/financial/mileage.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, validatePositiveNumber, sanitizeInput } from '../shared/validation'

/**
 * List mileage trips with optional filtering
 */
const _listMileageTrips = async (options = {}) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse([])
  }

  let query = supabase
    .from('mileage_trips')
    .select('id, trip_date, start_location, end_location, miles, purpose, client_id, job_id, notes, user_id, created_at, updated_at')
    .eq('company_id', companyId)
    .order('trip_date', { ascending: false })

  if (options.startDate) {
    query = query.gte('trip_date', options.startDate)
  }
  if (options.endDate) {
    query = query.lte('trip_date', options.endDate)
  }
  if (options.clientId) {
    query = query.eq('client_id', options.clientId)
  }
  if (options.jobId) {
    query = query.eq('job_id', options.jobId)
  }

  const { data, error } = await query
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new mileage trip
 */
const _createMileageTrip = async (tripData) => {
  validateRequired(tripData.trip_date, 'Trip date')
  validateRequired(tripData.miles, 'Miles')
  validatePositiveNumber(tripData.miles, 'Miles')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  const payload = {
    company_id: companyId,
    user_id: userId,
    trip_date: tripData.trip_date,
    start_location: tripData.start_location ? sanitizeInput(tripData.start_location) : null,
    end_location: tripData.end_location ? sanitizeInput(tripData.end_location) : null,
    miles: tripData.miles,
    purpose: tripData.purpose ? sanitizeInput(tripData.purpose) : null,
    client_id: tripData.client_id || null,
    job_id: tripData.job_id || null,
    notes: tripData.notes ? sanitizeInput(tripData.notes) : null
  }

  const { data, error } = await supabase
    .from('mileage_trips')
    .insert([payload])
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Update a mileage trip
 */
const _updateMileageTrip = async (tripId, updates) => {
  validateRequired(tripId, 'Trip ID')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  if (updates.miles !== undefined) {
    validatePositiveNumber(updates.miles, 'Miles')
  }

  const sanitizedData = {
    ...updates,
    updated_at: new Date().toISOString()
  }

  if (sanitizedData.start_location) {
    sanitizedData.start_location = sanitizeInput(sanitizedData.start_location)
  }
  if (sanitizedData.end_location) {
    sanitizedData.end_location = sanitizeInput(sanitizedData.end_location)
  }
  if (sanitizedData.purpose) {
    sanitizedData.purpose = sanitizeInput(sanitizedData.purpose)
  }
  if (sanitizedData.notes) {
    sanitizedData.notes = sanitizeInput(sanitizedData.notes)
  }

  const { data, error } = await supabase
    .from('mileage_trips')
    .update(sanitizedData)
    .eq('id', tripId)
    .eq('company_id', companyId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Delete a mileage trip
 */
const _deleteMileageTrip = async (tripId) => {
  validateRequired(tripId, 'Trip ID')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const { error } = await supabase
    .from('mileage_trips')
    .delete()
    .eq('id', tripId)
    .eq('company_id', companyId)

  if (error) throw error
  return { success: true }
}

/**
 * Get company mileage settings
 */
const _getMileageSettings = async () => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse({
      method: 'standard',
      rate_cents_per_mile: 67,
      fiscal_year: new Date().getFullYear()
    })
  }

  const { data, error } = await supabase
    .from('mileage_settings')
    .select('id, company_id, method, rate_cents_per_mile, fiscal_year, created_at, updated_at')
    .eq('company_id', companyId)
    .maybeSingle()

  if (error) throw error

  // Return defaults if no settings exist yet
  if (!data) {
    return createSuccessResponse({
      method: 'standard',
      rate_cents_per_mile: 67,
      fiscal_year: new Date().getFullYear()
    })
  }

  return createSuccessResponse(data)
}

/**
 * Create or update company mileage settings
 */
const _upsertMileageSettings = async (settings) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const payload = {
    company_id: companyId,
    method: settings.method || 'standard',
    rate_cents_per_mile: settings.rate_cents_per_mile ?? 67,
    fiscal_year: settings.fiscal_year || new Date().getFullYear(),
    updated_at: new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('mileage_settings')
    .upsert(payload, { onConflict: 'company_id' })
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Get annual mileage summary with monthly breakdown
 */
const _getMileageSummary = async (year) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse({
      totalMiles: 0,
      totalTrips: 0,
      totalDeduction: 0,
      avgMilesPerTrip: 0,
      monthlyBreakdown: []
    })
  }

  const startDate = `${year}-01-01`
  const endDate = `${year}-12-31`

  // Fetch all trips for the year
  const { data: trips, error: tripsError } = await supabase
    .from('mileage_trips')
    .select('id, trip_date, miles, client_id')
    .eq('company_id', companyId)
    .gte('trip_date', startDate)
    .lte('trip_date', endDate)
    .order('trip_date', { ascending: true })

  if (tripsError) throw tripsError

  // Fetch settings for rate
  const { data: settings } = await supabase
    .from('mileage_settings')
    .select('rate_cents_per_mile, method')
    .eq('company_id', companyId)
    .maybeSingle()

  const rateCentsPerMile = settings?.rate_cents_per_mile ?? 67
  const safeTrips = trips || []

  const totalMiles = safeTrips.reduce((sum, t) => sum + parseFloat(t.miles || 0), 0)
  const totalTrips = safeTrips.length
  const totalDeduction = (totalMiles * rateCentsPerMile) / 100
  const avgMilesPerTrip = totalTrips > 0 ? totalMiles / totalTrips : 0

  // Monthly breakdown
  const monthlyMap = {}
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  monthNames.forEach((name, index) => {
    monthlyMap[index] = { month: name, miles: 0, trips: 0 }
  })

  safeTrips.forEach((trip) => {
    const monthIndex = new Date(trip.trip_date + 'T00:00:00').getMonth()
    monthlyMap[monthIndex].miles += parseFloat(trip.miles || 0)
    monthlyMap[monthIndex].trips += 1
  })

  const monthlyBreakdown = Object.values(monthlyMap)

  // By-client breakdown
  const byClient = {}
  safeTrips.forEach((trip) => {
    const clientKey = trip.client_id || 'unassigned'
    if (!byClient[clientKey]) {
      byClient[clientKey] = { clientId: trip.client_id, miles: 0, trips: 0 }
    }
    byClient[clientKey].miles += parseFloat(trip.miles || 0)
    byClient[clientKey].trips += 1
  })

  return createSuccessResponse({
    totalMiles: Number(totalMiles.toFixed(1)),
    totalTrips,
    totalDeduction: Number(totalDeduction.toFixed(2)),
    avgMilesPerTrip: Number(avgMilesPerTrip.toFixed(1)),
    rateCentsPerMile,
    method: settings?.method || 'standard',
    monthlyBreakdown,
    byClient: Object.values(byClient)
  })
}

// Export wrapped functions
export const listMileageTrips = withErrorHandling(_listMileageTrips, 'list mileage trips', 'mileage_trips')
export const createMileageTrip = withErrorHandling(_createMileageTrip, 'create mileage trip', 'mileage_trip')
export const updateMileageTrip = withErrorHandling(_updateMileageTrip, 'update mileage trip', 'mileage_trip')
export const deleteMileageTrip = withErrorHandling(_deleteMileageTrip, 'delete mileage trip', 'mileage_trip')
export const getMileageSettings = withErrorHandling(_getMileageSettings, 'get mileage settings', 'mileage_settings')
export const upsertMileageSettings = withErrorHandling(_upsertMileageSettings, 'upsert mileage settings', 'mileage_settings')
export const getMileageSummary = withErrorHandling(_getMileageSummary, 'get mileage summary', 'mileage_summary')
